import { useEffect, useState } from 'react';
import type { AttributeDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useT } from '@/i18n/use-t';
import { Alert, Button, EmptyState, Pill, Skeleton } from '@/components/ui';
import { CartIcon } from '@/components/icons';
import { Section } from '@/components/page';

/**
 * Which questions this product asks its buyer (S11).
 *
 * The library itself is defined once elsewhere; here an operator picks the ones
 * this product offers and says which are required. A question inherited from
 * the product's category is already offered — this list is what the product
 * adds on top of that, which is why the empty state says so rather than
 * implying the product asks nothing.
 */
export function CustomerInputsCard({
  productId,
  onDraftChange,
}: {
  /** Set when editing a saved product. */
  productId?: string;
  /** Set instead while creating one; the product's save applies the choice. */
  onDraftChange?: (fieldIds: string[]) => void;
}) {
  const t = useT();

  const [library, setLibrary] = useState<AttributeDto[]>([]);
  const [assigned, setAssigned] = useState<Map<string, boolean>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void Promise.all([
      withSession(() => adminApi.productFields.list()),
      // Nothing is assigned yet while creating, so only the library is fetched.
      productId ? withSession(() => adminApi.productFields.forProduct(productId)) : null,
    ])
      .then(([all, mine]) => {
        if (cancelled) return;
        setLibrary(all.items.filter((field) => field.isActive));
        if (mine) setAssigned(new Map(mine.items.map((field) => [field.id, field.isRequired ?? false])));
      })
      .catch((cause) => !cancelled && setError(errorMessage(cause, 'Could not load the inputs.')))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [productId]);

  if (loading) return <Skeleton className="h-64" />;

  const save = async () => {
    setSaving(true);
    setError(null);

    if (!productId) return;

    try {
      await withSession(() =>
        adminApi.productFields.setForProduct(
          productId,
          [...assigned.entries()].map(([fieldId, isRequired]) => ({ fieldId, isRequired })),
        ),
      );
    } catch (cause) {
      setError(errorMessage(cause, 'Could not save the inputs.'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (fieldId: string) =>
    setAssigned((current) => {
      const next = new Map(current);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.set(fieldId, false);

      // While creating there is no save of its own; the editor collects this.
      if (!productId) onDraftChange?.([...next.keys()]);
      return next;
    });

  return (
    <Section title={t('customerInputs')} subtitle={t('customerInputsHint')} flush>
      {error ? (
        <div className="px-5 pb-3">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {library.length === 0 ? (
        <div className="border-t border-line">
          <EmptyState title={t('noCustomerInputs')} body={t('noCustomerInputsHint')} icon={<CartIcon />} />
        </div>
      ) : (
        <>
          <ul className="flex flex-col divide-y divide-line border-t border-line">
            {library.map((field) => {
              const isOn = assigned.has(field.id);

              return (
                <li key={field.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={isOn}
                      onChange={() => toggle(field.id)}
                      className="size-4 shrink-0"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm text-ink">{field.label}</span>
                      <span data-numeric dir="ltr" className="block text-xs text-ink-faint">
                        {field.code}
                      </span>
                    </span>
                  </label>

                  <Pill tone="neutral">{t(`attrType${field.type}` as 'attrTypeTEXT')}</Pill>

                  {/* Only meaningful once it is offered at all. */}
                  {isOn ? (
                    <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-muted">
                      <input
                        type="checkbox"
                        checked={assigned.get(field.id) ?? false}
                        onChange={(event) =>
                          setAssigned((current) => new Map(current).set(field.id, event.target.checked))
                        }
                        className="size-3.5"
                      />
                      {t('requiredAtCheckout')}
                    </label>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {productId ? (
            <div className="flex justify-end border-t border-line px-5 py-3">
              <Button size="sm" disabled={saving} onClick={() => void save()}>
                {saving ? t('saving') : t('save')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Section>
  );
}
